import { HttpErrorResponse } from "@angular/common/http";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { UploaderResponseError } from "../../../../shared/components/file-uploader/models/uploader-response-error";
import { ImageEntity } from "../../../../shared/entities/file/image.entity";
import {
  LinkedInPostContentType,
  PostPlatform,
  PostTargetPlatforms,
} from "../../../../shared/enums/campaignPost.enums";
import { AppDataPostTags } from "../../../../shared/models/appData";
import { BrandCampaign } from "../../../../shared/models/brandCampaign";
import { BrandCampaignCategory } from "../../../../shared/models/brandCampaignCategory";
import { BrandCampaignDetails } from "../../../../shared/models/brandCampaignDetail";
import { BrandCampaignLinkedInPost } from "../../../../shared/models/brandCampaignLinkedInPost";
import { AppDataService } from "../../../../shared/services/api/app-data.service";
import { cloneWith } from "../../../../shared/services/clonable";
import { DialogService } from "../../../../shared/services/dialog.service";
import { NotificationService } from "../../../../shared/services/notification.service";
import { BrandCampaignPostService } from "../../shared/services/brand-campaign-post.service";
import { LinkedinCarouselAdCreationDialogComponent } from "./linkedin-carousel-ad-creation-dialog/linkedin-carousel-ad-creation-dialog.component";
import {
  LinkedinMultiImagePostCreationDialogComponent,
  LinkedinMultiImagePostCreationDialogData,
} from "./linkedin-multi-image-creation-dialog/linkedin-multi-image-post-creation-dialog.component";
import {
  LinkedinImagePostCreationDialogComponent,
  LinkedinImagePostCreationDialogData,
} from "./linkedin-image-post-creation-dialog/linkedin-image-post-creation-dialog.component";
import {
  LinkedinVideoPostCreationDialogComponent,
  LinkedinVideoPostCreationDialogData,
} from "./linkedin-video-post-creation-dialog/linkedin-video-post-creation-dialog.component";

export type LinkedinPostCreationDialog =
  | LinkedinMultiImagePostCreationDialogComponent
  | LinkedinImagePostCreationDialogComponent
  | LinkedinVideoPostCreationDialogComponent
  | LinkedinCarouselAdCreationDialogComponent;

export interface LinkedInPostUploadData {
  media_type: LinkedInPostContentType;
  platform: PostPlatform;
  post_id?: string;
}

@Component({
  template: "",
  styleUrls: ["../campaign-content-creation-dialog.scss"],
})
export class LinkedinPostCreationBaseDialogComponent
  implements OnInit, OnDestroy
{
  protected campaign: BrandCampaign;
  protected campaignDetails: BrandCampaignDetails;
  protected category: BrandCampaignCategory;
  protected isNewPost: boolean;
  protected postType!: LinkedInPostContentType;
  public isSaving = false;
  public post: BrandCampaignLinkedInPost;
  public allValidPostTags?: AppDataPostTags[];
  public displayedPostTags?: AppDataPostTags[];
  public isSaveDisabled = false;
  private validButNotDisplayedPostTags: AppDataPostTags[] = [
    {
      tag: "{{campaign-custom-field}}",
      key: "campaign-custom-field",
    },
  ];
  public readonly LINKEDIN = PostTargetPlatforms.LinkedIn;
  protected readonly subscriptions = new Subscription();

  constructor(
    protected readonly brandCampaignPostService: BrandCampaignPostService,
    protected readonly dialogRef: MatDialogRef<LinkedinPostCreationDialog>,
    protected readonly dialogService: DialogService,
    protected readonly notificationService: NotificationService,
    protected readonly translateService: TranslateService,
    protected readonly dialog: MatDialog,
    protected readonly appDataService: AppDataService,
    @Inject(MAT_DIALOG_DATA)
    data:
      | LinkedinMultiImagePostCreationDialogData
      | LinkedinImagePostCreationDialogData
      | LinkedinVideoPostCreationDialogData,
  ) {
    this.campaign = data.campaign;
    this.campaignDetails = data.campaignDetails;
    this.category = data.category;
    this.isNewPost = !data.post;

    this.post = data.post
      ? cloneWith(data.post)
      : BrandCampaignLinkedInPost.create(data.contentType!);
  }

  public ngOnInit(): void {
    this.appDataService.get().then((appData) => {
      this.displayedPostTags = appData.postTags ?? [];
      this.allValidPostTags = [
        ...this.displayedPostTags,
        ...this.validButNotDisplayedPostTags,
      ];
    });

    this.checkSaveDisabled();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public onActionSave(): void {
    this.isSaving = true;
    if (this.isNewPost) {
      this.createPost();
    } else {
      this.savePost();
    }
  }

  public createPost(): void {
    this.isSaving = true;
    this.subscriptions.add(
      this.brandCampaignPostService
        .createBrandCampaignLinkedInPost(
          this.campaign.id,
          this.campaignDetails.id,
          this.category.id,
          this.post,
        )
        .subscribe({
          next: (post: BrandCampaignLinkedInPost) => {
            this.post = post;
            this.category.posts.push(this.post);
            this.dialogRef.close(post);
            this.notificationService.success("shared.changesSavedSuccessfully");
          },
          error: () => {
            this.isSaving = false;
            this.notificationService.error("shared.changesCouldNotBeSaved");
          },
        }),
    );
  }

  public savePost(): void {
    this.isSaving = true;
    this.subscriptions.add(
      this.brandCampaignPostService
        .updateBrandCampaignLinkedInPost(
          this.campaign.id,
          this.campaignDetails.id,
          this.category.id,
          this.post,
        )
        .subscribe({
          next: (post: BrandCampaignLinkedInPost) => {
            this.post = post;
            this.category.posts = this.category.posts.map((catPost) =>
              catPost.id === post.id ? post : catPost,
            );
            this.dialogRef.close(post);
            this.notificationService.success("shared.changesSavedSuccessfully");
          },
          error: () => {
            this.isSaving = false;
            this.notificationService.error("shared.changesCouldNotBeSaved");
          },
        }),
    );
  }

  public fileUploaderErrorHandler(error: UploaderResponseError): void {
    this.notificationService.error(
      this.translateService.instant(error.message, error.interpolateParams),
    );
  }

  public handleImageUploadResponse(
    apiResponse: ImageEntity | HttpErrorResponse,
  ): void {
    if (apiResponse instanceof HttpErrorResponse) {
      this.notificationService.error("shared.errorUploadingFile");
      return;
    }

    this.post.imageFileId = apiResponse.id;
    this.post.image = apiResponse.url;
    this.checkSaveDisabled();
    this.notificationService.success("shared.imageUploadedSuccessfully");
  }

  public checkSaveDisabled(): void {
    if (!this.isSaveDisabled) {
      this.isSaveDisabled = this.areTagsIncorrect;
      return;
    }
    this.isSaveDisabled = false;
  }

  protected get areTagsIncorrect(): boolean {
    // Check that the times that '{{' appear in the post text are the same times that a tag is included, so no tag has been modified
    return (
      this.allValidPostTags?.reduce(
        (total, tag) => total + this.post.text.split(tag.tag).length - 1,
        0,
      ) !==
      this.post.text.split("{{").length - 1
    );
  }

  public onActionPreview(): Promise<void> {
    return this.dialogService.showPostPreview({
      post: this.post,
    });
  }

  public onActionCancel(): void {
    this.dialogRef.close(false);
  }
}
